import React, { useEffect, useRef, useState } from 'react';
import { Container, Typography, Card, CardContent, Grid, Button, Box, TextField, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import dhsStudyService, { DHSStudyCard as DHSCard } from '../../services/dhsStudyService';

const DHSStudyHome: React.FC = () => {
  usePageTitle('DHS Study Center');
  
  const [cards, setCards] = useState<DHSCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<DHSCard>>({});
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await dhsStudyService.getCards();
        // If empty, optionally hydrate defaults client-side (user can add via UI)
        setCards(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (editingId && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [editingId]);

  const activateEdit = (card: DHSCard) => {
    setEditingId(card._id!);
    setForm({ ...card });
  };

  const onKeyActivate: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const id = (e.currentTarget as any).dataset.id as string;
      const card = cards.find((c) => c._id === id);
      if (card) activateEdit(card);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const updates: Partial<DHSCard> = {
      key: form.key!,
      title: form.title!,
      subtitle: form.subtitle!,
      route: form.route!,
      order: form.order ?? 0
    };
    const updated = await dhsStudyService.updateCard(editingId, updates);
    setCards((prev) => prev.map((c) => (c._id === editingId ? updated : c)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setEditingId(null);
    setForm({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({});
  };

  const deleteCard = async (id: string) => {
    await dhsStudyService.deleteCard(id);
    setCards((prev) => prev.filter((c) => c._id !== id));
  };

  const addCard = async () => {
    const base: DHSCard = {
      key: `card_${Date.now()}`,
      title: 'New Card',
      subtitle: 'Describe this section',
      route: '/dhs',
      order: (cards[cards.length - 1]?.order ?? cards.length) + 1
    };
    const created = await dhsStudyService.createCard(base);
    setCards((prev) => [...prev, created].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setEditingId(created._id!);
    setForm(created);
  };

  const onChange = (field: keyof DHSCard) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [field]: field === 'order' ? Number(e.target.value) : e.target.value }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h3" component="h1" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
        DHS Study Center
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        A structured, 508-compliant study hub covering DHS components, analysis topics, authoritative data sources, file definitions, system inventory, and mission value.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button onClick={addCard} variant="contained" sx={{ bgcolor: 'primary.main' }} aria-label="Add new study card">
          Add Card
        </Button>
      </Box>

      <Grid container spacing={3}>
        {loading ? (
          <Grid item xs={12}><Typography>Loading...</Typography></Grid>
        ) : (
          cards
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((card) => (
              <Grid item xs={12} md={6} key={card._id || card.key}>
                {editingId === card._id ? (
                  <Card sx={{ ':focus': { outline: '3px solid #005fcc' } }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <TextField
                          inputRef={firstInputRef}
                          label="Title"
                          value={form.title || ''}
                          onChange={onChange('title')}
                          fullWidth
                          inputProps={{ 'aria-label': 'Edit card title' }}
                        />
                        <TextField
                          label="Subtitle"
                          value={form.subtitle || ''}
                          onChange={onChange('subtitle')}
                          fullWidth
                          inputProps={{ 'aria-label': 'Edit card subtitle' }}
                        />
                        <TextField
                          label="Route"
                          value={form.route || ''}
                          onChange={onChange('route')}
                          fullWidth
                          inputProps={{ 'aria-label': 'Edit card route' }}
                        />
                        <TextField
                          label="Order"
                          type="number"
                          value={form.order ?? 0}
                          onChange={onChange('order')}
                          inputProps={{ 'aria-label': 'Edit card order' }}
                        />
                        <Stack direction="row" spacing={2}>
                          <Button onClick={saveEdit} variant="contained" sx={{ bgcolor: 'primary.main' }} aria-label="Save changes">
                            Save
                          </Button>
                          <Button onClick={cancelEdit} variant="outlined" aria-label="Cancel editing">
                            Cancel
                          </Button>
                          {card._id && (
                            <Button color="error" variant="outlined" onClick={() => deleteCard(card._id!)} aria-label="Delete card">
                              Delete
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    role="button"
                    tabIndex={0}
                    data-id={card._id}
                    aria-label={`Edit ${card.title}`}
                    onClick={() => activateEdit(card)}
                    onKeyDown={onKeyActivate}
                    sx={{ ':focus': { outline: '3px solid #005fcc' }, cursor: 'pointer' }}
                  >
                    <CardContent>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.title}</Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>{card.subtitle}</Typography>
                      <Button component={RouterLink} to={card.route} variant="contained" sx={{ bgcolor: 'primary.main' }}>
                        Open
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            ))
        )}
      </Grid>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Study Q&A</Typography>
        <Typography variant="body2">
          As we progress, we’ll capture key questions and answers within each section. Start with Components or Systems to anchor the catalog.
        </Typography>
      </Box>
    </Container>
  );
};

export default DHSStudyHome;
